import { useEffect, useState } from "react";
import CSDTable from "@cloudscape-design/components-themed/table";
import {
  Box,
  Header,
  SpaceBetween,
} from "@cloudscape-design/components-themed";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import { COMMON_LABELS } from "../default-constants";
import { LABELS } from "./constants";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import type { RootState } from "../../app/store";
import { useGetBankAccountsListQuery } from "../../features/api";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import { Link } from "react-router-dom";

interface ITable {
  isSkipSearch: boolean;
}

const Table = (props: ITable) => {
  const { isSkipSearch } = props;
  const dispatch = useAppDispatch();
  const { searchCriteria } = useAppSelector(
    (state: RootState) => state.bankAccountsList
  );
  const { data, isFetching } = useGetBankAccountsListQuery(searchCriteria, {
    skip: isSkipSearch,
  });

  /** テーブルカラム定義 */
  const columnDefinitions = [
    {
      id: "applicantCompanyName",
      header: LABELS.APPLICANT_COMPANY_NAME,
      cell: (e: any) => e.applicantCompanyName,
    },
    {
      id: "referenceGroupName",
      header: LABELS.REFERENCE_GROUP_NAME,
      cell: (e: any) => (
        <Link to={`/bank-accounts/${e.companyContractBankAccountNo}`}>
          {e.referenceGroupName}
        </Link>
      ),
    },
    {
      id: "paymentTargetCompanyName",
      header: LABELS.PAYMENT_TARGET_COMPANY_NAME,
      cell: (e: any) => e.paymentTargetCompanyName,
    },
    {
      id: "ipid",
      header: LABELS.IPID,
      cell: (e: any) =>
        e.ipid +
        (e.otherIpidNum === 0
          ? ""
          : LABELS.OTHER + e.otherIpidNum + LABELS.NUM),
    },
  ];

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);

  useEffect(() => {
    if (data !== undefined) {
      setPagedItems(getPagedItems(data, 1, preferences.pageSize));
      setAllItems(data);
      setCurrentPageIndex(1);
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, data, searchCriteria]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (allItems !== undefined) {
      setPagedItems(allItems.slice(0, newPageSize));
      setCurrentPageIndex(1);
    }
  };

  return (
    <div>
      <CSDTable
        columnDefinitions={columnDefinitions}
        loading={isFetching}
        loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
        items={pagedItems}
        resizableColumns
        header={
          <SpaceBetween size={"xs"}>
            <Header>
              {LABELS.PAYMENT_ACCOUNTS_LIST}
              {getHeaderCounterText(allItems.length, pagedItems.length)}
            </Header>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            filteredItems={allItems}
            setCurrentPageIndex={setCurrentPageIndex}
            pageSize={preferences.pageSize}
            setPagedItems={setPagedItems}
          />
        }
        preferences={
          <Preference
            preferences={preferences}
            options={options}
            handleOnConfirm={handleOnConfirm}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            {COMMON_LABELS.CSD.NO_DATA}
          </Box>
        }
      />
    </div>
  );
};

export default Table;
